import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

type Variant = "primary" | "money" | "secondary" | "ghost" | "danger" | "dark";
type Size = "sm" | "md" | "lg" | "pos";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  /** Right-aligned hint, e.g. the `F9` on the Pay button. */
  shortcut?: string;
  loading?: boolean;
  fullWidth?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-card",
  // Taking money, and only that: Pay at the till, Place Order in the
  // marketplace. Kept apart from `primary` so the button a cashier hits fifty
  // times a shift is not the same colour as every ordinary confirm — on a till
  // the green one is found by position and colour, rarely by reading it.
  money: "bg-money-500 text-white hover:bg-money-600 active:bg-money-700 shadow-card",
  secondary:
    "bg-surface text-ink-900 border border-line hover:bg-canvas active:bg-line/60",
  ghost: "bg-transparent text-ink-600 hover:bg-canvas active:bg-line/60",
  danger: "bg-surface text-danger-700 border border-danger-500/30 hover:bg-danger-50",
  dark: "bg-navy-800 text-white hover:bg-navy-700 active:bg-navy-900",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 px-3 text-sm gap-1.5 rounded-control",
  md: "h-touch px-4 text-sm gap-2 rounded-control",
  lg: "h-touch-lg px-5 text-base gap-2 rounded-control",
  // The tender row. Pressed by someone who is not looking at the screen.
  pos: "h-14 px-6 text-base font-semibold gap-2.5 rounded-control",
};

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  shortcut,
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      // `aria-busy` rather than swapping the label for a spinner: a screen
      // reader announces the state change without the button losing its name.
      aria-busy={loading || undefined}
      disabled={disabled ?? loading}
      className={cn(
        "inline-flex items-center justify-center font-medium",
        "transition-colors duration-100",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        SIZES[size],
        fullWidth && "w-full",
        className,
      )}
      {...props}
    >
      {loading ? (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      ) : (
        icon
      )}
      {children}
      {shortcut && (
        <span className="ml-1 text-[0.8em] font-normal opacity-70">({shortcut})</span>
      )}
    </button>
  );
}
